import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MiaV3Service } from '../services/mia-v3.service';
import { AutorizacaoService } from '../services/autorizacao.service';
import { custoEmReaisFormatado } from './custo-ia';

/**
 * Rótulo e recado de cada motivo de não-venda.
 *
 * O dono da loja não lê enum: ele lê "montou o carrinho e sumiu". A dica existe
 * porque número sem próximo passo não muda nada na operação.
 */
const MOTIVOS: { [chave: string]: { titulo: string, dica: string, destaque?: boolean } } = {
  ABANDONO_CARRINHO: {
    titulo: 'Montou o carrinho e sumiu',
    dica: 'Perda mais recuperável: essas pessoas já tinham escolhido o que queriam.',
    destaque: true
  },
  PAGAMENTO_FALHOU: {
    titulo: 'O pagamento não passou',
    dica: 'Se este número for alto, o problema está no meio de pagamento, não na conversa.'
  },
  FORA_AREA: {
    titulo: 'Endereço fora da área de entrega',
    dica: 'Perda real, mas fora do alcance da Mia — é decisão de área de cobertura.'
  },
  LOJA_FECHADA: {
    titulo: 'A loja estava fechada',
    dica: 'Vale olhar se a procura está concentrada em algum horário que ainda não abre.'
  },
  ITEM_INDISPONIVEL: {
    titulo: 'Procurou algo que você não tem',
    dica: 'Cada um destes é um pedido de cardápio que o cliente fez sem querer.'
  },
  DESISTENCIA: {
    titulo: 'Desistiu durante a conversa',
    dica: 'Desistência explícita costuma ser objeção de preço ou de tempo de entrega.'
  }
};

interface MotivoNaTela {
  chave: string;
  titulo: string;
  dica: string;
  destaque: boolean;
  quantidade: number;
  percentual: number;
}

/** Comparação com o período imediatamente anterior — sem limiar arbitrário. */
interface Tendencia {
  direcao: 'melhor' | 'pior' | 'igual';
  seta: string;
  texto: string;
}

interface AutorDeVenda {
  chave: 'mia' | 'cliente' | 'atendente';
  rotulo: string;
  quantidade: number;
  percentual: number;
}

@Component({
  selector: 'app-painel-mia-v3',
  templateUrl: './painel-mia-v3.component.html',
  styleUrls: ['./painel-mia-v3.component.scss']
})
export class PainelMiaV3Component implements OnInit {
  periodos = [
    { dias: 1, rotulo: 'Hoje' },
    { dias: 7, rotulo: '7 dias' },
    { dias: 30, rotulo: '30 dias' }
  ];

  diasSelecionados = 7;
  personalizado = false;
  /** Filtro personalizado com data E horário — os pickers do Kendo usam Date. */
  dataDe: Date | null = null;
  dataAte: Date | null = null;
  carregando = true;
  erro: string | null = null;

  resumo: any = null;
  tempos: any = null;
  anterior: any = null;
  motivos: MotivoNaTela[] = [];

  private usuario: any = {};

  constructor(private miaV3Service: MiaV3Service,
              private autorizacaoService: AutorizacaoService,
              private router: Router) {}

  ngOnInit(): void {
    this.usuario = this.autorizacaoService.getUser() || {};
    this.carregue();
  }

  /** Exportação de transcrição é sensível: só admin do MeuCardápio vê o botão. */
  get ehAdmin(): boolean {
    return !!(this.usuario.admin || this.usuario.superadmin);
  }

  selecionePeriodo(dias: number): void {
    if (dias === this.diasSelecionados && !this.personalizado) return;

    this.personalizado = false;
    this.diasSelecionados = dias;
    this.carregue();
  }

  /** Abre os campos de/até já preenchidos com o período do chip atual. */
  ativePeriodoPersonalizado(): void {
    if (this.personalizado) return;

    this.personalizado = true;

    // Dia inteiro por padrão: começa à meia-noite do primeiro dia e termina
    // no último minuto de hoje — quem quiser recortar o horário só ajusta.
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    inicio.setDate(inicio.getDate() - (this.diasSelecionados - 1));

    const fim = new Date();
    fim.setHours(23, 59, 0, 0);

    this.dataDe = inicio;
    this.dataAte = fim;
  }

  get periodoValido(): boolean {
    return !!this.dataDe && !!this.dataAte && this.dataDe.getTime() < this.dataAte.getTime();
  }

  apliquePeriodo(): void {
    if (!this.periodoValido) return;
    this.carregue();
  }

  carregue(): void {
    this.carregando = true;
    this.erro = null;

    this.miaV3Service.obtenhaMetricas(this.filtroAtual())
      .then(resposta => {
        this.resumo = resposta.resumo;
        this.tempos = resposta.tempos || null;
        this.anterior = resposta.anterior || null;
        this.motivos = this.monteMotivos(resposta.motivos);
      })
      .catch(() => this.erro = 'Não consegui carregar os números do atendimento agora.')
      .then(() => this.carregando = false);
  }

  /** Abre a lista de conversas já filtrada nas vendas perdidas deste período. */
  abraConversasNaoConvertidas(): void {
    const filtro = this.filtroAtual();
    const queryParams: any = { filtro: 'nao_converteu' };
    if (filtro.de && filtro.ate) {
      // a lista usa datas de dia inteiro; corta o horário do filtro do painel
      queryParams.de = filtro.de.slice(0, 10);
      queryParams.ate = filtro.ate.slice(0, 10);
    } else {
      queryParams.dias = filtro.dias;
    }
    this.router.navigate(['/admin/mia-v3/conversas'], { queryParams });
  }

  /** Baixa o CSV do mesmo período que está na tela (sessão autentica o GET). */
  exporteConversas(): void {
    window.location.href = this.miaV3Service.urlExportacaoDeConversas(this.filtroAtual());
  }

  /** Tela de QA do juiz (a rota também é guardada por admin do MeuCardápio). */
  abraQualidade(): void {
    this.router.navigate(['/admin/mia-v3/qualidade']);
  }

  private filtroAtual(): { dias?: number, de?: string, ate?: string } {
    if (this.personalizado && this.periodoValido) {
      return { de: this.formateInstante(this.dataDe), ate: this.formateInstante(this.dataAte) };
    }
    return { dias: this.diasSelecionados };
  }

  /** YYYY-MM-DDTHH:mm no fuso local — mesmo formato que o servidor lê. */
  private formateInstante(data: Date): string {
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    return `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}` +
      `T${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`;
  }

  private monteMotivos(motivos: any[]): MotivoNaTela[] {
    const maior = Math.max(1, ...motivos.map(motivo => Number(motivo.quantidade) || 0));

    return motivos
      .filter(motivo => MOTIVOS[motivo.motivo])
      .map(motivo => ({
        chave: motivo.motivo,
        titulo: MOTIVOS[motivo.motivo].titulo,
        dica: MOTIVOS[motivo.motivo].dica,
        destaque: !!MOTIVOS[motivo.motivo].destaque,
        quantidade: Number(motivo.quantidade) || 0,
        percentual: Math.round((Number(motivo.quantidade) || 0) / maior * 100)
      }));
  }

  get temDados(): boolean {
    return !!this.resumo && Number(this.resumo.atendimentos) > 0;
  }

  /** Denominador: só atendimentos em que o cliente queria comprar. */
  get elegiveis(): number {
    return Number(this.resumo?.elegiveis) || 0;
  }

  get vendas(): number {
    return Number(this.resumo?.vendas) || 0;
  }

  get conversao(): number {
    return this.elegiveis ? Math.round(this.vendas / this.elegiveis * 100) : 0;
  }

  get faturamento(): number {
    return Number(this.resumo?.faturamento) || 0;
  }

  get ticketMedio(): number {
    return this.vendas ? this.faturamento / this.vendas : 0;
  }

  get pedidosCancelados(): number {
    return Number(this.resumo?.pedidosCancelados) || 0;
  }

  get valorCancelado(): number {
    return Number(this.resumo?.valorCancelado) || 0;
  }

  get falhasTecnicas(): number {
    return Number(this.resumo?.falhasTecnicas) || 0;
  }

  get inconclusivos(): number {
    return Number(this.resumo?.inconclusivos) || 0;
  }

  get atendimentos(): number {
    return Number(this.resumo?.atendimentos) || 0;
  }

  get percentualInconclusivo(): number {
    return this.atendimentos ? Math.round(this.inconclusivos / this.atendimentos * 100) : 0;
  }

  /** Mandou uma mensagem e não voltou — o ruído natural do WhatsApp. */
  get contatosSemResposta(): number {
    return Number(this.resumo?.semResposta) || 0;
  }

  /** Conversou de verdade, mas sem pedido registrado pelo cardápio. */
  get conversasSemPedido(): number {
    return Math.max(0, this.inconclusivos - this.contatosSemResposta);
  }

  get escalados(): number {
    return Number(this.resumo?.escalados) || 0;
  }

  get emAndamento(): number {
    return Number(this.resumo?.emAndamento) || 0;
  }

  /** Conversão de quem passou por atendente humano, para comparar com a da Mia. */
  get conversaoEscalados(): number | null {
    const escalados = this.escalados;
    if (!escalados) return null;

    return Math.round((Number(this.resumo?.escaladosComVenda) || 0) / escalados * 100);
  }

  get perdas(): number {
    return Math.max(0, this.elegiveis - this.vendas);
  }

  get custoUsd(): number {
    return Number(this.resumo?.custoUsd) || 0;
  }

  get custoFormatado(): string {
    return custoEmReaisFormatado(this.custoUsd);
  }

  get custoPorAtendimento(): string | null {
    return this.atendimentos ? custoEmReaisFormatado(this.custoUsd / this.atendimentos) : null;
  }

  // ---------- quem fechou as vendas ----------

  get autoresDeVenda(): AutorDeVenda[] {
    if (!this.vendas) return [];

    const autores: AutorDeVenda[] = [
      { chave: 'mia', rotulo: 'Mia no chat', quantidade: Number(this.resumo?.vendasPelaMia) || 0, percentual: 0 },
      { chave: 'cliente', rotulo: 'Cliente pelo link', quantidade: Number(this.resumo?.vendasPeloCliente) || 0, percentual: 0 },
      { chave: 'atendente', rotulo: 'Atendente', quantidade: Number(this.resumo?.vendasPeloAtendente) || 0, percentual: 0 }
    ];

    for (const autor of autores) {
      autor.percentual = Math.round(autor.quantidade / this.vendas * 100);
    }

    return autores;
  }

  get vendasEscaladasPeloAtendente(): number {
    return Number(this.resumo?.vendasEscaladasPeloAtendente) || 0;
  }

  get escaladosComVenda(): number {
    return Number(this.resumo?.escaladosComVenda) || 0;
  }

  // ---------- velocidade ----------

  get esperaPeloAtendenteSeg(): number | null {
    const valor = this.tempos?.esperaPeloAtendenteSeg;
    return valor === null || valor === undefined ? null : Number(valor);
  }

  get respostaDoAtendenteSeg(): number | null {
    const valor = this.tempos?.respostaDoAtendenteSeg;
    return valor === null || valor === undefined ? null : Number(valor);
  }

  get esperasMedidas(): number {
    return Number(this.tempos?.esperasMedidas) || 0;
  }

  get esperaFormatada(): string {
    return this.formateDuracao(this.esperaPeloAtendenteSeg);
  }

  get respostaFormatada(): string {
    return this.formateDuracao(this.respostaDoAtendenteSeg);
  }

  /** Segundos numa duração legível: 45s, 4min, 1min 40s, 1h 05min. */
  formateDuracao(segundos: number | null): string {
    if (segundos === null) return '—';
    if (segundos < 60) return `${Math.max(0, Math.round(segundos))}s`;

    const minutosTotais = Math.round(segundos / 60);
    if (segundos < 3600) {
      const minutos = Math.floor(segundos / 60);
      const resto = Math.round(segundos - minutos * 60);
      return resto ? `${minutos}min ${resto}s` : `${minutos}min`;
    }

    const horas = Math.floor(minutosTotais / 60);
    const minutos = minutosTotais - horas * 60;
    return minutos ? `${horas}h ${String(minutos).padStart(2, '0')}min` : `${horas}h`;
  }

  // ---------- tendência contra o período anterior ----------

  private get resumoAnterior(): any {
    return this.anterior?.resumo || null;
  }

  private get temposAnteriores(): any {
    return this.anterior?.tempos || null;
  }

  get tendenciaConversao(): Tendencia | null {
    const elegiveisAntes = Number(this.resumoAnterior?.elegiveis) || 0;
    if (!elegiveisAntes || !this.elegiveis) return null;

    const antes = Math.round((Number(this.resumoAnterior?.vendas) || 0) / elegiveisAntes * 100);
    return this.compare(this.conversao, antes, `${antes}%`);
  }

  get tendenciaFaturamento(): Tendencia | null {
    const antes = Number(this.resumoAnterior?.faturamento) || 0;
    if (!antes) return null;

    const variacao = Math.round((this.faturamento - antes) / antes * 100);
    if (!variacao) return { direcao: 'igual', seta: '=', texto: 'igual ao período anterior' };
    return {
      direcao: variacao > 0 ? 'melhor' : 'pior',
      seta: variacao > 0 ? '▲' : '▼',
      texto: `${variacao > 0 ? '+' : ''}${variacao}% vs período anterior`
    };
  }

  get tendenciaAtendimentos(): Tendencia | null {
    const antes = Number(this.resumoAnterior?.atendimentos) || 0;
    if (!antes) return null;

    const variacao = Math.round((this.atendimentos - antes) / antes * 100);
    if (!variacao) return { direcao: 'igual', seta: '=', texto: 'igual ao período anterior' };
    return {
      direcao: variacao > 0 ? 'melhor' : 'pior',
      seta: variacao > 0 ? '▲' : '▼',
      texto: `${variacao > 0 ? '+' : ''}${variacao}% vs período anterior`
    };
  }

  get tendenciaEspera(): Tendencia | null {
    return this.compareTempo(this.esperaPeloAtendenteSeg, this.temposAnteriores?.esperaPeloAtendenteSeg);
  }

  get tendenciaResposta(): Tendencia | null {
    return this.compareTempo(this.respostaDoAtendenteSeg, this.temposAnteriores?.respostaDoAtendenteSeg);
  }

  /** Em tempo, MENOR é melhor — a palavra evita seta ambígua. */
  private compareTempo(atualSeg: number | null, anteriorSeg: any): Tendencia | null {
    if (atualSeg === null || anteriorSeg === null || anteriorSeg === undefined) return null;

    const antes = Number(anteriorSeg);
    const rotuloAntes = this.formateDuracao(antes);
    if (atualSeg === antes) return { direcao: 'igual', seta: '=', texto: `igual ao período anterior (${rotuloAntes})` };
    return {
      direcao: atualSeg < antes ? 'melhor' : 'pior',
      seta: atualSeg < antes ? '▲' : '▼',
      texto: `${atualSeg < antes ? 'melhor' : 'pior'} que antes (${rotuloAntes})`
    };
  }

  private compare(atual: number, antes: number, rotuloAntes: string): Tendencia {
    if (atual === antes) return { direcao: 'igual', seta: '=', texto: `igual ao período anterior (${rotuloAntes})` };
    return {
      direcao: atual > antes ? 'melhor' : 'pior',
      seta: atual > antes ? '▲' : '▼',
      texto: `${atual > antes ? 'melhor' : 'pior'} que antes (${rotuloAntes})`
    };
  }
}
